"use client";

import React, { useCallback, useEffect, useRef, useState } from "react";

import { cn } from "utils";
import { createPortal } from "react-dom";
import type { PopoverPosition, PopoverProps } from "./types";

export const Popover: React.FC<PopoverProps> = ({
  children,
  content,
  trigger = "click",
  placement = "bottom-start",
  open,
  onOpenChange,
  disabled = false,
  offset = 8,
  className,
  contentClassName,
  zIndex = 1000,
  destroyOnHide = false,
  getContainer,
}) => {
  const [isOpen, setIsOpen] = useState(open ?? false);
  const [position, setPosition] = useState<PopoverPosition>({ top: 0, left: 0 });
  
  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
   
  // @ts-ignore
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 计算弹框位置
  const calculatePosition = useCallback((): PopoverPosition => {
    if (!triggerRef.current) {return { top: 0, left: 0 };}

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    // 基础位置计算
    switch (placement) {
      case "top":
      case "top-start":
      case "top-end":
        top = triggerRect.top + scrollTop - offset;
        break;
      case "bottom":
      case "bottom-start":
      case "bottom-end":
        top = triggerRect.bottom + scrollTop + offset;
        break;
      case "left":
      case "left-start":
      case "left-end":
        left = triggerRect.left + scrollLeft - offset;
        break;
      case "right":
      case "right-start":
      case "right-end":
        left = triggerRect.right + scrollLeft + offset;
        break;
    }

    // 水平位置计算
    if (placement.includes("top") || placement.includes("bottom")) {
      if (placement.includes("start")) {
        left = triggerRect.left + scrollLeft;
      } else if (placement.includes("end")) {
        left = triggerRect.right + scrollLeft;
      } else {
        left = triggerRect.left + scrollLeft + triggerRect.width / 2;
      }
    }

    // 垂直位置计算
    if (placement.includes("left") || placement.includes("right")) {
      if (placement.includes("start")) {
        top = triggerRect.top + scrollTop;
      } else if (placement.includes("end")) {
        top = triggerRect.bottom + scrollTop;
      } else {
        top = triggerRect.top + scrollTop + triggerRect.height / 2;
      }
    }

    // 边界检测和调整
    if (contentRef.current) {
      const contentRect = contentRef.current.getBoundingClientRect();
      
      // 右边界检测
      if (left + contentRect.width > viewportWidth + scrollLeft) {
        left = viewportWidth + scrollLeft - contentRect.width - 8;
      }
      
      // 左边界检测
      if (left < scrollLeft) {
        left = scrollLeft + 8;
      }
      
      // 下边界检测
      if (top + contentRect.height > viewportHeight + scrollTop) {
        top = triggerRect.top + scrollTop - contentRect.height - offset;
      }
      
      // 上边界检测
      if (top < scrollTop) {
        top = triggerRect.bottom + scrollTop + offset;
      }
    }

    return { top, left };
  }, [placement, offset]);

  // 更新位置
  const updatePosition = useCallback(() => {
    if (isOpen) {
      const newPosition = calculatePosition();
      setPosition(newPosition);
    }
  }, [isOpen, calculatePosition]);

  // 处理显示状态变化
  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (disabled) {return;}
    
    setIsOpen(newOpen);
    onOpenChange?.(newOpen);
    
    if (newOpen) {
      // 延迟计算位置，确保内容已渲染
      requestAnimationFrame(() => {
        updatePosition();
      });
    }
  }, [disabled, onOpenChange, updatePosition]);

  // 处理触发器事件
  const handleTriggerClick = useCallback(() => {
    if (trigger === "click") {
      handleOpenChange(!isOpen);
    }
  }, [trigger, isOpen, handleOpenChange]);

  const handleTriggerMouseEnter = useCallback(() => {
    if (trigger === "hover") {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      handleOpenChange(true);
    }
  }, [trigger, handleOpenChange]);

  const handleTriggerMouseLeave = useCallback(() => {
    if (trigger === "hover") {
      timerRef.current = setTimeout(() => {
        handleOpenChange(false);
      }, 100);
    }
  }, [trigger, handleOpenChange]);

  const handleContentMouseEnter = useCallback(() => {
    if (trigger === "hover" && timerRef.current) {
      clearTimeout(timerRef.current);
    }
  }, [trigger]);

  const handleContentMouseLeave = useCallback(() => {
    if (trigger === "hover") {
      timerRef.current = setTimeout(() => {
        handleOpenChange(false);
      }, 100);
    }
  }, [trigger, handleOpenChange]);

  // 监听外部点击
  useEffect(() => {
    if (trigger !== "click" || !isOpen) {return;}

    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        handleOpenChange(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [trigger, isOpen, handleOpenChange]);

  // 监听窗口大小变化和滚动
  useEffect(() => {
    if (!isOpen) {return;}

    const handleResize = () => updatePosition();
    const handleScroll = () => updatePosition();

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen, updatePosition]);

  // 受控状态同步
  useEffect(() => {
    if (open !== undefined && open !== isOpen) {
      setIsOpen(open);
    }
  }, [open, isOpen]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // 渲染弹框内容
  const renderContent = () => {
    if (!isOpen && destroyOnHide) {return null;}

    const containerElement = getContainer ? getContainer() : document.body;

    const popoverContent = (
      <div
        ref={contentRef}
        className={cn(
          "fixed bg-background_solid_secondary rounded-lg shadow-lg border border-border_primary",
          "opacity-0 invisible transition-all duration-200 ease-out",
          isOpen && "opacity-100 visible",
          contentClassName,
        )}
        style={{
          top: position.top,
          left: position.left,
          zIndex,
        }}
        onMouseEnter={handleContentMouseEnter}
        onMouseLeave={handleContentMouseLeave}
      >
        {content}
      </div>
    );

    return createPortal(popoverContent, containerElement);
  };

  return (
    <div className={cn("inline-block", className)}>
      <div
        ref={triggerRef}
        onClick={handleTriggerClick}
        onMouseEnter={handleTriggerMouseEnter}
        onMouseLeave={handleTriggerMouseLeave}
        className="cursor-pointer"
      >
        {children}
      </div>
      {renderContent()}
    </div>
  );
};